// doctor-frontend/src/pages/approval/ApprovalEditor.js
// Phase 1 structural alignment (no regression):
// - Reposition as returned-prescription correction page only
// - Keep main approval flow in Reviewer Workbench
// - Keep main operational flow in Comprehensive Services
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { Box, Paper, Typography, TextField, Chip, Stack, Alert, Button } from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import http from '../../services/http'; // tenant-safe token handled in http client

function useQuery() {
  const { search } = useLocation();
  return useMemo(() => new URLSearchParams(search), [search]);
}

// ✅ tenant-safe navigation base
function tenantBase() {
  try {
    const m = (window.location.pathname || '').match(/^\/t\/[^/]+/);
    return m ? m[0] : '';
  } catch {
    return '';
  }
}

// -------------------- Medicine helpers --------------------
// Backend Visit model expects: prescription.medicines = [ { drug, dose, sig, frequency, duration, route, notes } ]
function normalizeMedicines(raw) {
  if (!Array.isArray(raw)) return [];
  return raw
    .map((m) => {
      if (!m) return null;
      if (typeof m === 'string') {
        const t = m.trim();
        if (!t) return null;
        return { drug: t };
      }
      if (typeof m === 'object') {
        // Keep known fields; tolerate older shapes like { text } or { name }
        const drug =
          (typeof m.drug === 'string' && m.drug.trim()) ||
          (typeof m.name === 'string' && m.name.trim()) ||
          (typeof m.text === 'string' && m.text.trim()) ||
          '';
        const dose = typeof m.dose === 'string' ? m.dose : '';
        const sig = typeof m.sig === 'string' ? m.sig : '';
        const frequency = typeof m.frequency === 'string' ? m.frequency : '';
        const duration = typeof m.duration === 'string' ? m.duration : '';
        const route = typeof m.route === 'string' ? m.route : '';
        const notes = typeof m.notes === 'string' ? m.notes : '';

        if (!drug && !dose && !sig && !frequency && !duration && !route && !notes) return null;

        return {
          drug,
          dose,
          sig,
          frequency,
          duration,
          route,
          notes,
        };
      }
      return null;
    })
    .filter(Boolean);
}

function medicineToLine(m) {
  if (!m) return '';
  if (typeof m === 'string') return m;

  const drug = (m.drug || m.name || m.text || '').toString().trim();
  const dose = (m.dose || '').toString().trim();
  const sig = (m.sig || '').toString().trim();
  const frequency = (m.frequency || '').toString().trim();
  const duration = (m.duration || '').toString().trim();
  const route = (m.route || '').toString().trim();
  const notes = (m.notes || '').toString().trim();

  // Render compactly but readably (non-breaking)
  const left = [drug, dose].filter(Boolean).join(' ');
  const right = [sig || frequency, duration, route].filter(Boolean).join(' ');
  const tail = notes ? ` (${notes})` : '';

  if (left && right) return `${left} — ${right}${tail}`;
  if (left) return `${left}${tail}`;
  if (right) return `${right}${tail}`;
  return tail.replace(/^ /, '');
}

// Parse a single line into a medicine object. Minimal, safe parser:
// - If user writes: "Drug Name — 1 tab BD x 5 days", we store drug + sig.
// - Otherwise store drug=line.
function lineToMedicine(line) {
  const t = String(line || '').trim();
  if (!t) return null;

  // Split on em-dash or hyphen dash patterns
  const parts = t.split(/\s+—\s+|\s+-\s+/).map((x) => x.trim()).filter(Boolean);

  if (parts.length >= 2) {
    return {
      drug: parts[0],
      sig: parts.slice(1).join(' — '), // keep remainder in sig to avoid data loss
    };
  }

  return { drug: t };
}

export default function ApprovalEditor() {
  const query = useQuery();
  const navigate = useNavigate();
  const visitId = query.get('visitId') || '';

  const [status, setStatus] = useState('returned');
  const [medicines, setMedicines] = useState([]); // always objects for backend safety
  const [advice, setAdvice] = useState('');
  const [differentialDx, setDifferentialDx] = useState('');
  const [icdCodes, setIcdCodes] = useState([]);
  const [msg, setMsg] = useState({ type: '', text: '' });
  const [busy, setBusy] = useState(false);

  const approvalsPath = useMemo(
    () => `${tenantBase()}/dashboard/approvals?tab=returned&mine=true`,
    []
  );
  const workbenchPath = useMemo(
    () => `${tenantBase()}/dashboard/approval?tab=returned`,
    []
  );
  const operationsPath = useMemo(
    () => `${tenantBase()}/dashboard/operations`,
    []
  );

  // Load prescription to edit (token handled by http client)
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        if (!visitId) return;

        let data = null;
        try {
          const r = await http.get(`/api/visits/${encodeURIComponent(visitId)}/prescription`);
          data = r?.data?.prescription || r?.data || null;
        } catch {
          const r = await http.get(`/api/visit-prescription/${encodeURIComponent(visitId)}`);
          data = r?.data?.prescription || r?.data || null;
        }

        if (cancelled || !data) return;

        setStatus(data.status || 'returned');

        // ✅ normalize medicines into objects to match Visit model
        const medsRaw =
          data.medicines ||
          data.lines?.map((x) => x?.text).filter(Boolean) || // legacy read shape
          [];
        setMedicines(normalizeMedicines(medsRaw));

        setAdvice(data.advice || '');
        setDifferentialDx(data.differentialDx || data.differentialDiagnosis || '');
        setIcdCodes(Array.isArray(data.icdCodes) ? data.icdCodes : []);
      } catch {
        // swallow; UI shows empty for safety
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [visitId]);

  const saveAndResubmit = useCallback(async () => {
    try {
      setBusy(true);
      setMsg({ type: '', text: '' });

      if (!visitId) {
        setMsg({ type: 'error', text: 'Missing visitId. Please open this from Approvals → Returned.' });
        return;
      }

      const payload = {
        medicines: normalizeMedicines(medicines), // ✅ ensure embedded schema compatibility
        advice,
        differentialDx,
        icdCodes,
        status: 'returned',
      };

      // Save (new → legacy)
      try {
        await http.put(`/api/visits/${encodeURIComponent(visitId)}/prescription`, { prescription: payload });
      } catch {
        await http.put(`/api/visit-prescription/${encodeURIComponent(visitId)}`, payload);
      }

      // Resubmit
      try {
        await http.patch(`/api/visits/${encodeURIComponent(visitId)}/prescription/submit`, {});
      } catch {
        await http.patch(`/api/visit-prescription/${encodeURIComponent(visitId)}/pending`, {});
      }

      setMsg({ type: 'success', text: 'Resubmitted for approval.' });

      // ✅ tenant-safe navigation (prevents “falling out” of /t/:slug)
      navigate(approvalsPath, { replace: true });
    } catch (e) {
      setMsg({ type: 'error', text: e?.message || 'Error while resubmitting.' });
    } finally {
      setBusy(false);
    }
  }, [visitId, medicines, advice, differentialDx, icdCodes, navigate, approvalsPath]);

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h5" sx={{ mb: 1 }}>
        Returned Prescription Editor
      </Typography>
      <Typography variant="body2" sx={{ mb: 2, opacity: 0.8 }}>
        Use this page only to correct a returned prescription and resubmit it. Reviewer Workbench remains the main approval surface, and Comprehensive Services remains the main operational hub.
      </Typography>

      <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: 'wrap' }}>
        <Button variant="outlined" size="small" onClick={() => navigate(operationsPath)}>
          Comprehensive Services
        </Button>
        <Button variant="outlined" size="small" onClick={() => navigate(workbenchPath)}>
          Reviewer Workbench
        </Button>
      </Stack>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <Chip label={(status || 'RETURNED').toUpperCase()} color="error" variant="outlined" />
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            Make fixes here, then resubmit.
          </Typography>
        </Stack>
      </Paper>

      {!visitId && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          visitId is missing. Please open this screen via Approvals → Returned → Edit.
        </Alert>
      )}

      {msg.text && (
        <Alert severity={msg.type === 'error' ? 'error' : 'success'} sx={{ mb: 2 }}>
          {msg.text}
        </Alert>
      )}

      <Paper sx={{ p: 2 }}>
        <Typography variant="h6">Prescription</Typography>

        <TextField
          label="Differential Diagnosis"
          fullWidth
          multiline
          minRows={2}
          sx={{ mt: 1 }}
          value={differentialDx}
          onChange={(e) => setDifferentialDx(e.target.value)}
        />

        <TextField
          label="Advice"
          fullWidth
          multiline
          minRows={2}
          sx={{ mt: 2 }}
          value={advice}
          onChange={(e) => setAdvice(e.target.value)}
        />

        <TextField
          label="Medicines (one per line)"
          fullWidth
          multiline
          minRows={6}
          sx={{ mt: 2 }}
          value={(Array.isArray(medicines) ? medicines : []).map(medicineToLine).filter(Boolean).join('\n')}
          onChange={(e) => {
            const next = e.target.value
              .split('\n')
              .map((s) => s.trim())
              .filter(Boolean)
              .map(lineToMedicine)
              .filter(Boolean);
            setMedicines(next);
          }}
        />

        <TextField
          label="ICD codes (comma separated, e.g. I10,E11.9)"
          fullWidth
          sx={{ mt: 2 }}
          value={(Array.isArray(icdCodes) ? icdCodes : []).map((c) => (c?.code || c)).filter(Boolean).join(',')}
          onChange={(e) =>
            setIcdCodes(
              e.target.value
                .split(',')
                .map((s) => s.trim())
                .filter(Boolean)
                .map((code) => ({ code }))
            )
          }
        />

        <Stack direction="row" spacing={2} sx={{ mt: 3 }}>
          <Button variant="contained" disabled={busy || !visitId} onClick={saveAndResubmit}>
            Resubmit for Approval
          </Button>

          <Button
            variant="outlined"
            disabled={busy}
            onClick={() => navigate(workbenchPath, { replace: true })}
          >
            Back to Reviewer Workbench
          </Button>
        </Stack>
      </Paper>
    </Box>
  );
}
